import React, { useEffect, useState } from 'react'
import { Link, Redirect } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'
import { toast } from 'react-toastify'
import { Helmet } from 'react-helmet'

import { Heading, NotFoundMessage, UnauthorisedMessage } from '@peracto/peracto-ui'
import { GET_ONE, UPDATE, useClient, getSchemaFromResource } from '@peracto/client'

const AttributeGroupEditContainer = ({ children }) => {
    return (
        <div className="form-container">
            <Heading name="Edit Attribute Group">
                <div className="!grow !flex !items-center !justify-end">
                    <Link
                        className="btn btn-outline-primary"
                        to="/attribute-groups"
                        data-testid="back-to-attribute-groups"
                    >
                        <FontAwesomeIcon icon={faArrowCircleLeft} className="!mr-2" />
                        Back to Attribute Groups
                    </Link>
                </div>
            </Heading>
            {children}
        </div>
    )
}

const AttributeGroupEdit = ({ AttributeGroupForm, location: { pathname } }) => {
    const { client, getResource } = useClient()
    const [loading, setLoading] = useState(true)
    const [unauthorised, setUnauthorised] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const [redirect, setRedirect] = useState()

    const [attributeGroup, setAttributeGroup] = useState()
    const [attributes, setAttributes] = useState()

    const fetchAttributeGroup = async () => {
        try {
            const { data, response } = await client(GET_ONE, 'attribute-groups', {
                id: pathname,
            })

            setAttributeGroup(data)
            setAttributes(response.json.attributes)
            setLoading(false)

            if (response.status === 404) {
                setRedirect('/attribute-groups')
            }
        } catch (e) {
            console.error(e)
            if (e.status === 403) {
                setUnauthorised(true)
            }

            if (e.status === 404) {
                setNotFound(true)
            }
            setLoading(false)
            setRedirect('/attribute-groups')
        }
    }

    useEffect(() => {
        fetchAttributeGroup()
    }, [])

    const schema = getSchemaFromResource(getResource('attribute-groups'))

    const onSubmit = async (data, actions) => {
        const formData = {
            ...data,
            attributes: data.attributes.map(attr => attr['@id']),
        }

        try {
            const response = await client(UPDATE, 'attribute-groups', {
                id: pathname,
                data: formData,
            })

            if (response.data.violations && response.data.violations.length > 0) {
                // Display errors for invalid fields
                actions.setSubmitting(false)
                response.data.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            } else {
                actions.setSubmitting(false)
                toast.success('Attribute Group successfully updated!')
            }
        } catch (e) {
            console.error(e)
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
            if (e?.error?.body?.violations?.length > 0) {
                // Display errors for invalid fields
                e.error.body.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            }

            actions.setSubmitting(false)
        }
    }

    if (loading) {
        return (
            <AttributeGroupEditContainer>
                <div className="card">
                    <div className="card-body">Loading...</div>
                </div>
            </AttributeGroupEditContainer>
        )
    }

    if (notFound) {
        return (
            <NotFoundMessage
                url="/attribute-groups"
                message="The attribute group you're looking for could not be found"
                buttonLabel="Go to Attribute Groups"
            />
        )
    }

    if (unauthorised) {
        return <UnauthorisedMessage />
    }

    return (
        <AttributeGroupEditContainer>
            {redirect && <Redirect to={redirect} />}

            <Helmet>
                <title>{attributeGroup.name || 'Attribute Groups'} | Edit | Peracto</title>
            </Helmet>

            <AttributeGroupForm
                values={attributeGroup}
                groupAttributes={attributes}
                onSubmit={onSubmit}
                schema={schema}
            />
        </AttributeGroupEditContainer>
    )
}

export default AttributeGroupEdit
